import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';
import {
  BookOpen,
  FileText,
  CreditCard,
  Building,
  PieChart,
  BarChart3,
  Plus,
  Pencil,
  Trash2,
  Eye,
  Check,
  X,
  ChevronRight,
  ChevronDown,
  Search,
  Filter,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownLeft,
  Wallet,
  TrendingUp,
  Calendar,
  DollarSign
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const TABS = [
  { id: 'dashboard', name: 'Dashboard', icon: BarChart3 },
  { id: 'coa', name: 'Chart of Accounts', icon: BookOpen },
  { id: 'ledgers', name: 'Ledgers', icon: FileText },
  { id: 'vouchers', name: 'Vouchers', icon: CreditCard },
  { id: 'cost-centers', name: 'Cost Centers', icon: Building },
  { id: 'budgets', name: 'Budgets', icon: PieChart },
  { id: 'reports', name: 'Reports', icon: BarChart3 },
];

const VOUCHER_TYPES = [
  { value: 'Purchase', label: 'Purchase', color: 'bg-orange-500' },
  { value: 'Sales', label: 'Sales', color: 'bg-green-500' },
  { value: 'Payment', label: 'Payment', color: 'bg-red-500' },
  { value: 'Receipt', label: 'Receipt', color: 'bg-blue-500' },
  { value: 'Journal', label: 'Journal', color: 'bg-purple-500' },
  { value: 'Contra', label: 'Contra', color: 'bg-gray-500' },
];

const ACCOUNT_TYPES = [
  { value: 'Assets', label: 'Assets', color: 'text-blue-600' },
  { value: 'Liabilities', label: 'Liabilities', color: 'text-red-600' },
  { value: 'Income', label: 'Income', color: 'text-green-600' },
  { value: 'Expenses', label: 'Expenses', color: 'text-orange-600' },
  { value: 'Capital', label: 'Capital', color: 'text-purple-600' },
];

function Accounting({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  const [loading, setLoading] = useState(false);

  // Data states
  const [dashboardStats, setDashboardStats] = useState(null);
  const [accountGroups, setAccountGroups] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [budgets, setBudgets] = useState([]);

  // Filters
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [voucherTypeFilter, setVoucherTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showCostCenterModal, setShowCostCenterModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [ledgerForm, setLedgerForm] = useState({
    name: '', group_id: '', opening_balance: 0, opening_balance_type: 'Debit',
    gst_number: '', pan_number: '', contact_person: '', phone: '', email: '', address: ''
  });
  const [voucherForm, setVoucherForm] = useState({
    voucher_type: 'Payment', voucher_date: new Date().toISOString().split('T')[0],
    party_ledger_id: '', narration: '', line_items: [
      { ledger_id: '', debit_amount: 0, credit_amount: 0, narration: '' },
      { ledger_id: '', debit_amount: 0, credit_amount: 0, narration: '' }
    ]
  });
  const [groupForm, setGroupForm] = useState({
    name: '', code: '', account_type: 'Assets', nature: 'Debit', parent_id: ''
  });
  const [costCenterForm, setCostCenterForm] = useState({
    name: '', code: '', category: 'Department', parent_id: ''
  });
  const [budgetForm, setBudgetForm] = useState({
    name: '', fiscal_year: '2025-26', cost_center_id: '', total_amount: 0
  });

  // Expanded groups (for tree view)
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'dashboard':
          await fetchDashboard();
          break;
        case 'coa':
          await fetchAccountGroups();
          break;
        case 'ledgers':
          await fetchLedgers();
          break;
        case 'vouchers':
          await fetchVouchers();
          break;
        case 'cost-centers':
          await fetchCostCenters();
          break;
        case 'budgets':
          await fetchBudgets();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const fetchDashboard = async () => {
    const response = await axios.get(`${API}/api/accounting/dashboard`, getAuthConfig());
    setDashboardStats(response.data);
  };

  const fetchAccountGroups = async () => {
    const params = {};
    if (accountTypeFilter !== 'all') params.account_type = accountTypeFilter;
    const response = await axios.get(`${API}/api/accounting/account-groups`, { params, ...getAuthConfig() });
    setAccountGroups(response.data.account_groups || []);
  };

  const fetchLedgers = async () => {
    const params = { limit: 200 };
    if (searchQuery) params.search = searchQuery;
    const response = await axios.get(`${API}/api/accounting/ledgers`, { params, ...getAuthConfig() });
    setLedgers(response.data.ledgers || []);
  };

  const fetchVouchers = async () => {
    const params = { limit: 100 };
    if (voucherTypeFilter !== 'all') params.voucher_type = voucherTypeFilter;
    const response = await axios.get(`${API}/api/accounting/vouchers`, { params, ...getAuthConfig() });
    setVouchers(response.data.vouchers || []);
  };

  const fetchCostCenters = async () => {
    const response = await axios.get(`${API}/api/accounting/cost-centers`, getAuthConfig());
    setCostCenters(response.data.cost_centers || []);
  };

  const fetchBudgets = async () => {
    const response = await axios.get(`${API}/api/accounting/budgets`, getAuthConfig());
    setBudgets(response.data.budgets || []);
  };

  // CREATE functions
  const createLedger = async () => {
    if (!ledgerForm.name || !ledgerForm.group_id) {
      alert('Please fill in Ledger Name and Account Group');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/api/accounting/ledgers`, ledgerForm, getAuthConfig());
      setShowLedgerModal(false);
      setLedgerForm({
        name: '', group_id: '', opening_balance: 0, opening_balance_type: 'Debit',
        gst_number: '', pan_number: '', contact_person: '', phone: '', email: '', address: ''
      });
      fetchLedgers();
      alert('Ledger created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create ledger');
    } finally {
      setLoading(false);
    }
  };

  const createVoucher = async () => {
    if (!voucherForm.voucher_type || voucherForm.line_items.length < 2) {
      alert('Please fill in voucher details with at least 2 line items');
      return;
    }
    // Validate debit = credit
    const totalDebit = voucherForm.line_items.reduce((sum, item) => sum + (parseFloat(item.debit_amount) || 0), 0);
    const totalCredit = voucherForm.line_items.reduce((sum, item) => sum + (parseFloat(item.credit_amount) || 0), 0);
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      alert(`Debit (₹${totalDebit}) must equal Credit (₹${totalCredit})`);
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/api/accounting/vouchers`, {
        ...voucherForm,
        total_amount: totalDebit
      }, getAuthConfig());
      setShowVoucherModal(false);
      setVoucherForm({
        voucher_type: 'Payment', voucher_date: new Date().toISOString().split('T')[0],
        party_ledger_id: '', narration: '', line_items: [
          { ledger_id: '', debit_amount: 0, credit_amount: 0, narration: '' },
          { ledger_id: '', debit_amount: 0, credit_amount: 0, narration: '' }
        ]
      });
      fetchVouchers();
      alert('Voucher created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create voucher');
    } finally {
      setLoading(false);
    }
  };

  const createAccountGroup = async () => {
    if (!groupForm.name || !groupForm.code || !groupForm.account_type) {
      alert('Please fill in Name, Code, and Account Type');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/api/accounting/account-groups`, groupForm, getAuthConfig());
      setShowGroupModal(false);
      setGroupForm({ name: '', code: '', account_type: 'Assets', nature: 'Debit', parent_id: '' });
      fetchAccountGroups();
      alert('Account Group created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create account group');
    } finally {
      setLoading(false);
    }
  };

  const createCostCenter = async () => {
    if (!costCenterForm.name || !costCenterForm.code) {
      alert('Please fill in Name and Code');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/api/accounting/cost-centers`, costCenterForm, getAuthConfig());
      setShowCostCenterModal(false);
      setCostCenterForm({ name: '', code: '', category: 'Department', parent_id: '' });
      fetchCostCenters();
      alert('Cost Center created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create cost center');
    } finally {
      setLoading(false);
    }
  };

  const createBudget = async () => {
    if (!budgetForm.name || !budgetForm.cost_center_id || !budgetForm.total_amount) {
      alert('Please fill in Name, Cost Center, and Amount');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/api/accounting/budgets`, budgetForm, getAuthConfig());
      setShowBudgetModal(false);
      setBudgetForm({ name: '', fiscal_year: '2025-26', cost_center_id: '', total_amount: 0 });
      fetchBudgets();
      alert('Budget created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create budget');
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    setVoucherForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { ledger_id: '', debit_amount: 0, credit_amount: 0, narration: '' }]
    }));
  };

  const removeLineItem = (index) => {
    if (voucherForm.line_items.length <= 2) {
      alert('Minimum 2 line items required for double-entry');
      return;
    }
    setVoucherForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setVoucherForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const seedDefaults = async (type) => {
    if (!window.confirm(`Seed default ${type}? This will add standard entries.`)) return;
    
    try {
      if (type === 'coa') {
        await axios.post(`${API}/api/accounting/account-groups/seed-defaults`, {}, getAuthConfig());
      } else if (type === 'cost-centers') {
        await axios.post(`${API}/api/accounting/cost-centers/seed-defaults`, {}, getAuthConfig());
      }
      loadData();
      alert('Default entries seeded successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to seed defaults');
    }
  };

  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Accounting Dashboard</h2>
          <p className="text-gray-500">Fiscal Year: {dashboardStats?.fiscal_year || 'Loading...'}</p>
        </div>
        <button
          onClick={() => loadData()}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Ledgers</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardStats?.total_ledgers || 0}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Vouchers</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardStats?.total_vouchers || 0}</p>
              <p className="text-xs text-orange-600">{dashboardStats?.pending_vouchers || 0} pending</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <CreditCard className="text-orange-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Month Receipts</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(dashboardStats?.month_receipts)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <ArrowDownLeft className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Month Payments</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(dashboardStats?.month_payments)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <ArrowUpRight className="text-red-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Net Cash Flow */}
      <div className="bg-gradient-to-r from-[#2B3380] to-[#4a5fc1] rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-white/80">Net Cash Flow (This Month)</p>
            <p className="text-3xl font-bold">
              {formatCurrency(dashboardStats?.net_cash_flow)}
            </p>
          </div>
          <TrendingUp size={48} className="text-white/30" />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => { setActiveTab('vouchers'); setShowVoucherModal(true); }}
            className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
          >
            <CreditCard className="text-[#2B3380] mb-2" size={20} />
            <p className="font-medium text-gray-900">New Voucher</p>
            <p className="text-xs text-gray-500">Create entry</p>
          </button>
          <button
            onClick={() => { setActiveTab('ledgers'); setShowLedgerModal(true); }}
            className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
          >
            <FileText className="text-[#2B3380] mb-2" size={20} />
            <p className="font-medium text-gray-900">New Ledger</p>
            <p className="text-xs text-gray-500">Create account</p>
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
          >
            <BarChart3 className="text-[#2B3380] mb-2" size={20} />
            <p className="font-medium text-gray-900">Trial Balance</p>
            <p className="text-xs text-gray-500">View report</p>
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
          >
            <PieChart className="text-[#2B3380] mb-2" size={20} />
            <p className="font-medium text-gray-900">P&L Statement</p>
            <p className="text-xs text-gray-500">View report</p>
          </button>
        </div>
      </div>
    </div>
  );

  const renderAccountGroupTree = (groups, level = 0) => {
    return groups.map(group => {
      const hasChildren = group.children && group.children.length > 0;
      const isExpanded = expandedGroups.has(group.id);
      
      return (
        <div key={group.id}>
          <div
            className={`flex items-center py-2 px-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100`}
            style={{ paddingLeft: `${level * 24 + 12}px` }}
            onClick={() => hasChildren && toggleGroup(group.id)}
          >
            {hasChildren ? (
              isExpanded ? <ChevronDown size={16} className="text-gray-400 mr-2" /> : <ChevronRight size={16} className="text-gray-400 mr-2" />
            ) : (
              <span className="w-4 mr-2" />
            )}
            <span className="font-mono text-xs text-gray-400 mr-3">{group.code}</span>
            <span className={`flex-1 ${level === 0 ? 'font-semibold' : ''} ${
              ACCOUNT_TYPES.find(t => t.value === group.account_type)?.color || 'text-gray-900'
            }`}>
              {group.name}
            </span>
            <span className="text-xs text-gray-400">{group.nature}</span>
          </div>
          {hasChildren && isExpanded && renderAccountGroupTree(group.children, level + 1)}
        </div>
      );
    });
  };

  const renderCOA = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Chart of Accounts</h2>
        <div className="flex gap-2">
          <button
            onClick={() => seedDefaults('coa')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Seed Defaults
          </button>
          <button
            onClick={() => setShowGroupModal(true)}
            className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] flex items-center gap-2"
          >
            <Plus size={16} />
            Add Group
          </button>
        </div>
      </div>

      {/* Filter by Account Type */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => { setAccountTypeFilter('all'); fetchAccountGroups(); }}
          className={`px-4 py-2 rounded-lg font-medium ${
            accountTypeFilter === 'all' ? 'bg-[#2B3380] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {ACCOUNT_TYPES.map(type => (
          <button
            key={type.value}
            onClick={() => { setAccountTypeFilter(type.value); }}
            className={`px-4 py-2 rounded-lg font-medium ${
              accountTypeFilter === type.value ? 'bg-[#2B3380] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Account Groups Tree */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center">
          <span className="w-6" />
          <span className="font-mono text-xs text-gray-500 w-24">Code</span>
          <span className="flex-1 font-semibold text-gray-700">Account Name</span>
          <span className="text-xs text-gray-500 w-20">Nature</span>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
              Loading...
            </div>
          ) : accountGroups.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <BookOpen className="mx-auto mb-2 text-gray-300" size={48} />
              <p>No account groups found. Click &quot;Seed Defaults&quot; to create standard chart of accounts.</p>
            </div>
          ) : (
            renderAccountGroupTree(accountGroups)
          )}
        </div>
      </div>
    </div>
  );

  const renderLedgers = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Ledgers</h2>
        <button
          onClick={() => { setEditingItem(null); setShowLedgerModal(true); }}
          className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] flex items-center gap-2"
        >
          <Plus size={16} />
          New Ledger
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search ledgers by name or code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchLedgers()}
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
        />
      </div>

      {/* Ledgers Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Code</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Ledger Name</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Group</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Source</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Opening Bal.</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                    Loading...
                  </td>
                </tr>
              ) : ledgers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    <FileText className="mx-auto mb-2 text-gray-300" size={48} />
                    <p>No ledgers found</p>
                  </td>
                </tr>
              ) : ledgers.map(ledger => (
                <tr key={ledger.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-sm text-gray-500">{ledger.code}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{ledger.name}</p>
                    {ledger.gst_number && <p className="text-xs text-gray-500">GST: {ledger.gst_number}</p>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{ledger.group_name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      ledger.source_module === 'Manual' ? 'bg-gray-100 text-gray-600' :
                      ledger.source_module === 'Supplier' ? 'bg-orange-100 text-orange-600' :
                      ledger.source_module === 'Customer' ? 'bg-blue-100 text-blue-600' :
                      'bg-purple-100 text-purple-600'
                    }`}>
                      {ledger.source_module}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {ledger.opening_balance > 0 && (
                      <span className={ledger.opening_balance_type === 'Debit' ? 'text-blue-600' : 'text-red-600'}>
                        {formatCurrency(ledger.opening_balance)} {ledger.opening_balance_type === 'Debit' ? 'Dr' : 'Cr'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button className="p-2 text-gray-600 hover:text-[#2B3380] hover:bg-gray-100 rounded-lg" title="View">
                        <Eye size={16} />
                      </button>
                      <button className="p-2 text-gray-600 hover:text-[#2B3380] hover:bg-gray-100 rounded-lg" title="Edit">
                        <Pencil size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderVouchers = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Vouchers</h2>
        <button
          onClick={() => { setEditingItem(null); setShowVoucherModal(true); }}
          className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] flex items-center gap-2"
        >
          <Plus size={16} />
          New Voucher
        </button>
      </div>

      {/* Voucher Type Filter */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => { setVoucherTypeFilter('all'); }}
          className={`px-4 py-2 rounded-lg font-medium ${
            voucherTypeFilter === 'all' ? 'bg-[#2B3380] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {VOUCHER_TYPES.map(type => (
          <button
            key={type.value}
            onClick={() => setVoucherTypeFilter(type.value)}
            className={`px-4 py-2 rounded-lg font-medium ${
              voucherTypeFilter === type.value ? 'bg-[#2B3380] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Vouchers Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Voucher No.</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Date</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Type</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Party</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Amount</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                    Loading...
                  </td>
                </tr>
              ) : vouchers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    <CreditCard className="mx-auto mb-2 text-gray-300" size={48} />
                    <p>No vouchers found. Create your first voucher.</p>
                  </td>
                </tr>
              ) : vouchers.map(voucher => {
                const typeInfo = VOUCHER_TYPES.find(t => t.value === voucher.voucher_type);
                return (
                  <tr key={voucher.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">{voucher.voucher_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{voucher.voucher_date}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs text-white ${typeInfo?.color}`}>
                        {voucher.voucher_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{voucher.party_name || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono font-medium">
                      {formatCurrency(voucher.total_amount)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        voucher.status === 'Approved' ? 'bg-green-100 text-green-700' :
                        voucher.status === 'Draft' ? 'bg-yellow-100 text-yellow-700' :
                        voucher.status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {voucher.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button className="p-2 text-gray-600 hover:text-[#2B3380] hover:bg-gray-100 rounded-lg" title="View">
                          <Eye size={16} />
                        </button>
                        {voucher.status === 'Draft' && (
                          <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Approve">
                            <Check size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderCostCenterTree = (centers, level = 0) => {
    return centers.map(center => {
      const hasChildren = center.children && center.children.length > 0;
      const isExpanded = expandedGroups.has(center.id);
      
      return (
        <div key={center.id}>
          <div
            className={`flex items-center py-3 px-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100`}
            style={{ paddingLeft: `${level * 24 + 16}px` }}
            onClick={() => hasChildren && toggleGroup(center.id)}
          >
            {hasChildren ? (
              isExpanded ? <ChevronDown size={16} className="text-gray-400 mr-2" /> : <ChevronRight size={16} className="text-gray-400 mr-2" />
            ) : (
              <span className="w-4 mr-2" />
            )}
            <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded mr-3">{center.code}</span>
            <span className={`flex-1 ${level === 0 ? 'font-semibold' : ''} text-gray-900`}>
              {center.name}
            </span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{center.category}</span>
          </div>
          {hasChildren && isExpanded && renderCostCenterTree(center.children, level + 1)}
        </div>
      );
    });
  };

  const renderCostCenters = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Cost Centers</h2>
        <div className="flex gap-2">
          <button
            onClick={() => seedDefaults('cost-centers')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Seed Defaults
          </button>
          <button
            onClick={() => setShowCostCenterModal(true)}
            className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] flex items-center gap-2"
          >
            <Plus size={16} />
            Add Cost Center
          </button>
        </div>
      </div>

      {/* Cost Centers Tree */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center">
          <span className="w-6" />
          <span className="font-mono text-xs text-gray-500 w-24">Code</span>
          <span className="flex-1 font-semibold text-gray-700">Cost Center Name</span>
          <span className="text-xs text-gray-500 w-24">Category</span>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
              Loading...
            </div>
          ) : costCenters.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Building className="mx-auto mb-2 text-gray-300" size={48} />
              <p>No cost centers found. Click &quot;Seed Defaults&quot; to create standard cost centers.</p>
            </div>
          ) : (
            renderCostCenterTree(costCenters)
          )}
        </div>
      </div>
    </div>
  );

  const renderBudgets = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Budgets</h2>
        <button
          onClick={() => setShowBudgetModal(true)}
          className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] flex items-center gap-2"
        >
          <Plus size={16} />
          Create Budget
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-500">
        <PieChart className="mx-auto mb-4 text-gray-300" size={64} />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Budget Management</h3>
        <p className="text-gray-500 mb-4">Create and track budgets against actual expenses</p>
        <button 
          onClick={() => setShowBudgetModal(true)}
          className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0]"
        >
          Create Your First Budget
        </button>
      </div>
    </div>
  );

  const renderReports = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Financial Reports</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Trial Balance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BarChart3 className="text-blue-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Trial Balance</h3>
              <p className="text-sm text-gray-500">Debit & Credit summary</p>
            </div>
          </div>
          <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            Generate Report
          </button>
        </div>

        {/* Profit & Loss */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="text-green-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Profit & Loss</h3>
              <p className="text-sm text-gray-500">Income vs Expenses</p>
            </div>
          </div>
          <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            Generate Report
          </button>
        </div>

        {/* Balance Sheet */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Wallet className="text-purple-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Balance Sheet</h3>
              <p className="text-sm text-gray-500">Assets & Liabilities</p>
            </div>
          </div>
          <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            Generate Report
          </button>
        </div>

        {/* Ledger Statement */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <FileText className="text-orange-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Ledger Statement</h3>
              <p className="text-sm text-gray-500">Individual account details</p>
            </div>
          </div>
          <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            Select Ledger
          </button>
        </div>

        {/* Day Book */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <Calendar className="text-indigo-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Day Book</h3>
              <p className="text-sm text-gray-500">Daily transactions</p>
            </div>
          </div>
          <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            Select Date
          </button>
        </div>

        {/* GST Reports */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <DollarSign className="text-red-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">GST Reports</h3>
              <p className="text-sm text-gray-500">GSTR-1, GSTR-3B</p>
            </div>
          </div>
          <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            Coming Soon
          </button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'coa':
        return renderCOA();
      case 'ledgers':
        return renderLedgers();
      case 'vouchers':
        return renderVouchers();
      case 'cost-centers':
        return renderCostCenters();
      case 'budgets':
        return renderBudgets();
      case 'reports':
        return renderReports();
      default:
        return renderDashboard();
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg overflow-x-auto">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSearchParams({ tab: tab.id });
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-[#2B3380] shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon size={18} />
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {renderContent()}
      </div>

      {/* ========== MODALS ========== */}
      
      {/* Ledger Modal */}
      {showLedgerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Create New Ledger</h2>
              <button onClick={() => setShowLedgerModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ledger Name *</label>
                  <input
                    type="text"
                    value={ledgerForm.name}
                    onChange={(e) => setLedgerForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                    placeholder="e.g., ABC Suppliers Pvt Ltd"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Group *</label>
                  <select
                    value={ledgerForm.group_id}
                    onChange={(e) => setLedgerForm(prev => ({ ...prev, group_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                  >
                    <option value="">Select Group</option>
                    {accountGroups.flatMap(g => [
                      <option key={g.id} value={g.id}>{g.name}</option>,
                      ...(g.children || []).map(c => (
                        <option key={c.id} value={c.id}>↳ {c.name}</option>
                      ))
                    ])}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label>
                  <input
                    type="number"
                    value={ledgerForm.opening_balance}
                    onChange={(e) => setLedgerForm(prev => ({ ...prev, opening_balance: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Balance Type</label>
                  <select
                    value={ledgerForm.opening_balance_type}
                    onChange={(e) => setLedgerForm(prev => ({ ...prev, opening_balance_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                  >
                    <option value="Debit">Debit</option>
                    <option value="Credit">Credit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                  <input
                    type="text"
                    value={ledgerForm.gst_number}
                    onChange={(e) => setLedgerForm(prev => ({ ...prev, gst_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="27AABCT1234A1Z5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                  <input
                    type="text"
                    value={ledgerForm.pan_number}
                    onChange={(e) => setLedgerForm(prev => ({ ...prev, pan_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="ABCDE1234F"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={ledgerForm.contact_person}
                    onChange={(e) => setLedgerForm(prev => ({ ...prev, contact_person: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={ledgerForm.phone}
                    onChange={(e) => setLedgerForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={ledgerForm.email}
                    onChange={(e) => setLedgerForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    value={ledgerForm.address}
                    onChange={(e) => setLedgerForm(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={2}
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowLedgerModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={createLedger}
                disabled={loading}
                className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Ledger'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voucher Modal */}
      {showVoucherModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Create New Voucher</h2>
              <button onClick={() => setShowVoucherModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Voucher Type *</label>
                  <select
                    value={voucherForm.voucher_type}
                    onChange={(e) => setVoucherForm(prev => ({ ...prev, voucher_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B3380]"
                  >
                    {VOUCHER_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={voucherForm.voucher_date}
                    onChange={(e) => setVoucherForm(prev => ({ ...prev, voucher_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2B3380]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Party (Optional)</label>
                  <select
                    value={voucherForm.party_ledger_id}
                    onChange={(e) => setVoucherForm(prev => ({ ...prev, party_ledger_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select Party</option>
                    {ledgers.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Line Items (Double-Entry)</label>
                  <button
                    onClick={addLineItem}
                    className="text-sm text-[#2B3380] hover:underline flex items-center gap-1"
                  >
                    <Plus size={14} /> Add Row
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-600">Ledger</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-600 w-32">Debit (₹)</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-600 w-32">Credit (₹)</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-600">Narration</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {voucherForm.line_items.map((item, index) => (
                        <tr key={index} className="border-t border-gray-100">
                          <td className="px-2 py-2">
                            <select
                              value={item.ledger_id}
                              onChange={(e) => updateLineItem(index, 'ledger_id', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                            >
                              <option value="">Select Ledger</option>
                              {ledgers.map(l => (
                                <option key={l.id} value={l.id}>{l.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              value={item.debit_amount || ''}
                              onChange={(e) => updateLineItem(index, 'debit_amount', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm text-right"
                              placeholder="0.00"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              value={item.credit_amount || ''}
                              onChange={(e) => updateLineItem(index, 'credit_amount', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm text-right"
                              placeholder="0.00"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="text"
                              value={item.narration || ''}
                              onChange={(e) => updateLineItem(index, 'narration', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                              placeholder="Description"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <button
                              onClick={() => removeLineItem(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-medium">
                        <td className="px-3 py-2 text-right text-sm">Total:</td>
                        <td className="px-3 py-2 text-right text-sm text-blue-600">
                          ₹{voucherForm.line_items.reduce((s, i) => s + (parseFloat(i.debit_amount) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right text-sm text-red-600">
                          ₹{voucherForm.line_items.reduce((s, i) => s + (parseFloat(i.credit_amount) || 0), 0).toLocaleString()}
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Narration</label>
                <textarea
                  value={voucherForm.narration}
                  onChange={(e) => setVoucherForm(prev => ({ ...prev, narration: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="Enter voucher description..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowVoucherModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={createVoucher}
                disabled={loading}
                className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Voucher'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Create Account Group</h2>
              <button onClick={() => setShowGroupModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group Name *</label>
                <input
                  type="text"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Bank Accounts"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={groupForm.code}
                  onChange={(e) => setGroupForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono"
                  placeholder="e.g., A-CA-BANK"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Type *</label>
                  <select
                    value={groupForm.account_type}
                    onChange={(e) => setGroupForm(prev => ({ 
                      ...prev, 
                      account_type: e.target.value,
                      nature: ['Assets', 'Expenses'].includes(e.target.value) ? 'Debit' : 'Credit'
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {ACCOUNT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nature</label>
                  <select
                    value={groupForm.nature}
                    onChange={(e) => setGroupForm(prev => ({ ...prev, nature: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Debit">Debit</option>
                    <option value="Credit">Credit</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Group (Optional)</label>
                <select
                  value={groupForm.parent_id}
                  onChange={(e) => setGroupForm(prev => ({ ...prev, parent_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">None (Primary Group)</option>
                  {accountGroups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowGroupModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button onClick={createAccountGroup} disabled={loading} className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] disabled:opacity-50">
                {loading ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cost Center Modal */}
      {showCostCenterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Create Cost Center</h2>
              <button onClick={() => setShowCostCenterModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={costCenterForm.name}
                  onChange={(e) => setCostCenterForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Mumbai Store"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={costCenterForm.code}
                  onChange={(e) => setCostCenterForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono"
                  placeholder="e.g., CC-MUM-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={costCenterForm.category}
                  onChange={(e) => setCostCenterForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Department">Department</option>
                  <option value="Project">Project</option>
                  <option value="Location">Location</option>
                  <option value="Product Line">Product Line</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent (Optional)</label>
                <select
                  value={costCenterForm.parent_id}
                  onChange={(e) => setCostCenterForm(prev => ({ ...prev, parent_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">None (Root Level)</option>
                  {costCenters.flatMap(c => [
                    <option key={c.id} value={c.id}>{c.name}</option>,
                    ...(c.children || []).map(ch => (
                      <option key={ch.id} value={ch.id}>↳ {ch.name}</option>
                    ))
                  ])}
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowCostCenterModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button onClick={createCostCenter} disabled={loading} className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] disabled:opacity-50">
                {loading ? 'Creating...' : 'Create Cost Center'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Budget Modal */}
      {showBudgetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Create Budget</h2>
              <button onClick={() => setShowBudgetModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Budget Name *</label>
                <input
                  type="text"
                  value={budgetForm.name}
                  onChange={(e) => setBudgetForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Marketing Q1 2026"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fiscal Year</label>
                  <select
                    value={budgetForm.fiscal_year}
                    onChange={(e) => setBudgetForm(prev => ({ ...prev, fiscal_year: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="2024-25">2024-25</option>
                    <option value="2025-26">2025-26</option>
                    <option value="2026-27">2026-27</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount *</label>
                  <input
                    type="number"
                    value={budgetForm.total_amount}
                    onChange={(e) => setBudgetForm(prev => ({ ...prev, total_amount: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="100000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost Center *</label>
                <select
                  value={budgetForm.cost_center_id}
                  onChange={(e) => setBudgetForm(prev => ({ ...prev, cost_center_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select Cost Center</option>
                  {costCenters.flatMap(c => [
                    <option key={c.id} value={c.id}>{c.name}</option>,
                    ...(c.children || []).map(ch => (
                      <option key={ch.id} value={ch.id}>↳ {ch.name}</option>
                    ))
                  ])}
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowBudgetModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button onClick={createBudget} disabled={loading} className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] disabled:opacity-50">
                {loading ? 'Creating...' : 'Create Budget'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Accounting;
